import { ReactElement, useState } from 'react';
import { Modal, Spinner } from '@weareopenr/component-library';
import dayjs from 'dayjs';
import { FieldValues, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'src/routes';
import { useStepNavigation } from 'src/hooks/useStepNavigation/useStepNavigation';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useTimetable } from 'src/hooks/useTimetable';
import { useTimetablePublishDestinations } from 'src/hooks/useTimetablePublishDestinations/useTimetablePublishDestinations';
import { usePublishTimetable } from 'src/hooks/usePublishTimetable/usePublishTimetable';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { useValidate } from 'src/hooks/useValidate/useValidate';
import { getValidationRules } from 'src/utils/schedules/getValidationRules';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { NoChannelsView } from 'src/components/PublishModal/NoChannelsView/NoChannelsView';
import PublishView from 'src/components/PublishModal/PublishView';
import PublishFlowFooter from 'src/components/PublishModal/PublishFlowFooter/PublishFlowFooter';
import { IPublishDestination } from 'src/components/PublishModal/types';
import { buildPublishSelection } from 'src/components/PublishModal/utilities/buildPublishSelection/buildPublishSelection';
import { getVenuesWithChannels } from 'src/components/PublishModal/utilities/getVenuesWithChannels/getVenuesWithChannels';
import { destinationId } from 'src/components/PublishModal/utilities/destinationId/destinationId';
import { formatScheduledTime, publishTimes } from 'src/components/PublishDetailsView/shared';
import ModalImg from 'src/assets/images/chef-plating.png';
import TimetableSetupView from './TimetableSetupView/TimetableSetupView';
import TimetableSummaryView from './TimetableSummaryView/TimetableSummaryView';
import { errorNotification, successNotification } from './Notifications';
import { IModalProps, TimetablePublishSteps } from './types';
import styles from '../PublishModal/PublishModal.module.scss';

const steps = [TimetablePublishSteps.Setup, TimetablePublishSteps.Venues, TimetablePublishSteps.Summary];

const getPrimaryLabel = (isSummaryStep: boolean, isScheduled: boolean): string => {
  if (!isSummaryStep) return 'Continue';
  return isScheduled ? 'Schedule publish' : 'Publish now';
};

const TimetablePublishFlowWrapper = ({
  timetableId,
  timetableName,
  closeModal,
}: {
  timetableId: string;
  timetableName: string;
  closeModal: () => void;
}) => {
  const { data: timetableData, isLoading: isTimetableLoading, byEntity } = useTimetable(timetableId);
  const { data: publicationData, isLoading: isPublicationDataLoading, isError } = useTimetablePublishDestinations();

  if (isTimetableLoading || isPublicationDataLoading) {
    return (
      <div className={styles['publish-flow']}>
        <Spinner size="large" data-testid="publish-loading" />
      </div>
    );
  }

  if (!publicationData || publicationData.length === 0 || isError || !timetableData) {
    return (
      <div className={styles['publish-flow']}>
        <NoChannelsView />
      </div>
    );
  }

  return (
    <TimetablePublishFlow
      timetableId={timetableId}
      timetableName={timetableName}
      closeModal={closeModal}
      publicationData={publicationData}
      entityGroups={byEntity}
    />
  );
};

const TimetablePublishFlow = ({
  timetableId,
  timetableName,
  closeModal,
  publicationData,
  entityGroups,
}: {
  timetableId: string;
  timetableName: string;
  publicationData: IPublishDestination[];
  entityGroups: ReturnType<typeof useTimetable>['byEntity'];
  closeModal: () => void;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { trackEvent, captureException } = useUserTracking();

  const publishTimetable = usePublishTimetable(timetableId);
  const validate = useValidate();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { step, incrementStep, decrementStep } = useStepNavigation(steps, TimetablePublishSteps.Setup);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultSelectedVenues = Array.from(
    new Set(publicationData.flatMap((destination) => destination.venues).map((venue) => venue.venue_id)),
  );

  const defaultValues = {
    publicationType: publishTimes.NOW,
    publicationDate: '',
    scheduleName: timetableName,
    selectedChannels: [],
    venues: defaultSelectedVenues,
    confirmationCheckbox: false,
  };

  const formFields = useForm<FieldValues>({
    defaultValues,
  });
  const { control, watch, register, setValue, handleSubmit } = formFields;

  const getButtonDisabledState = (values: FieldValues, step: TimetablePublishSteps) => {
    switch (step) {
      case TimetablePublishSteps.Setup: {
        const hasChannel = (values['selectedChannels'] ?? []).length > 0;
        const scheduleReady =
          values['publicationType'] !== publishTimes.SCHEDULE ||
          (Boolean(values['confirmationCheckbox']) &&
            dayjs(values['publicationDate']).isValid() &&
            dayjs(values['publicationDate']).isAfter(dayjs()));
        return !hasChannel || !scheduleReady;
      }
      case TimetablePublishSteps.Venues:
        return getVenuesWithChannels(publicationData, values['selectedChannels'], values['venues']).length === 0;
      case TimetablePublishSteps.Summary:
        return (
          isSubmitting ||
          getVenuesWithChannels(publicationData, values['selectedChannels'], values['venues']).length === 0
        );
      default:
        return false;
    }
  };

  const isSummaryStep = step === TimetablePublishSteps.Summary;
  const isScheduled = watch('publicationType') === publishTimes.SCHEDULE;
  // Summary and Setup close the modal; only the Venues step steps back.
  const leftButtonIsBack = step === TimetablePublishSteps.Venues;
  const leftButtonAction = leftButtonIsBack ? decrementStep : closeModal;

  const channelNameDict = publicationData.map((destination) => [destinationId(destination), destination.name]);

  const values = watch();
  const continueDisabled = getButtonDisabledState(values, step);
  const selectedChannelIds: string[] = values['selectedChannels'] ?? [];

  const handlePublish = async (values: FieldValues) => {
    clearError();
    setIsSubmitting(true);

    const selectedVenueIds: Record<string, boolean> = Object.fromEntries(
      ((values['venues'] ?? []) as string[]).map((venueId) => [venueId, true]),
    );

    const publication_date_time = formatScheduledTime(values['publicationType'], dayjs(values['publicationDate']));

    try {
      const { channels: instances, channelIds } = buildPublishSelection(
        publicationData,
        values['selectedChannels'],
        selectedVenueIds,
      );

      const names = channelNameDict.filter(([channel_id]) => channelIds.includes(channel_id)).map((c) => c.at(1));

      const response = await publishTimetable.mutateAsync({
        instances: instances.map(({ instance_id, venues }) => ({ instance_id: instance_id as string, venues })),
        ...(publication_date_time && {
          publication_date_time: new Date(publication_date_time).toISOString(),
          schedule_name: values['scheduleName'],
        }),
      });

      const isScheduled = values['publicationType'] === publishTimes.SCHEDULE;

      // Validation only applies to scheduled timetable publishes - publishing
      // immediately has no schedule to attach a report to. This fires
      // fire-and-forget: the report is viewed later from the schedule card,
      // not blocking the publish success flow here.
      if (isScheduled && response?.schedule_id) {
        validate.mutate({
          criteria: {
            schedule_id: response.schedule_id,
            timetable_id: timetableId,
            rules: getValidationRules(EVENT_TYPES.PUBLISH_TIMETABLE),
          },
        });
      }

      trackEvent('timetable_published', {
        timetableId,
        timetableName,
        channelCount: instances.length,
        isScheduled,
      });

      showNotification(successNotification(timetableName, names, publication_date_time));
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.timetable, getBrandId(), timetableId] });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (isScheduled) {
        closeModal();
      } else {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        navigate(`${Routes.TIMETABLES}/${timetableId}/status`);
      }
    } catch (error) {
      setIsSubmitting(false);
      captureException(error, { tags: { action: 'publish_timetable', timetableId } });
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      return showNotification(errorNotification(timetableName, publication_date_time, errorMsg));
    }
  };

  const primaryLabel = getPrimaryLabel(isSummaryStep, isScheduled);

  return (
    <div className={styles['publish-flow']}>
      <main>
        <form onSubmit={handleSubmit(handlePublish)}>
          {step === TimetablePublishSteps.Setup && (
            <TimetableSetupView
              control={control}
              register={register}
              watch={watch}
              timetableName={timetableName}
              publicationData={publicationData}
            />
          )}
          {step === TimetablePublishSteps.Venues && (
            <PublishView
              selectedVenueIds={values['venues'] ?? []}
              onVenuesChange={(venueIds) => setValue('venues', venueIds, { shouldDirty: true })}
              publicationData={publicationData}
              selectedChannelIds={selectedChannelIds}
              eyebrow="Timetable Publish"
            />
          )}
          {step === TimetablePublishSteps.Summary && (
            <TimetableSummaryView
              watch={watch}
              timetableName={timetableName}
              publicationData={publicationData}
              entityGroups={entityGroups}
            />
          )}
          <PublishFlowFooter
            leftButtonIsBack={leftButtonIsBack}
            onLeftButtonClick={leftButtonAction}
            isSummaryStep={isSummaryStep}
            onIncrementStep={incrementStep}
            continueDisabled={continueDisabled}
            isSubmitting={isSubmitting}
            primaryLabel={primaryLabel}
            errorMessage={errorMessage}
          />
        </form>
      </main>
    </div>
  );
};

const TimetablePublishModal = ({ closeModal, isOpen, timetableId, timetableName }: IModalProps): ReactElement => {
  return (
    <Modal closeModal={closeModal} open={isOpen} image={ModalImg} imageAlt="Picture of a chef plating up">
      <TimetablePublishFlowWrapper timetableId={timetableId} timetableName={timetableName} closeModal={closeModal} />
    </Modal>
  );
};

export default TimetablePublishModal;
